/** 端末 / 従業員を選ぶ Select。入力はサーバーの `q=` で絞る(`filterOption={false}`)。 */
import { useQuery } from "@tanstack/react-query";
import { Select } from "antd";
import { useState } from "react";

import { useApi } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";
import { useDlpDevices } from "./hooks";

/**
 * 端末を選ぶ Select。入力をサーバーの `q=` で絞る(`filterOption={false}`)。
 * スキャン依頼・一覧の条件で同じものを使う。
 */
export function DeviceSelect({
  value,
  onChange,
  placeholder = "ホスト名 / 製造番号",
  autoFocus,
}: {
  /** Form.Item の子にするときは渡さない(Form が value / onChange を注入する) */
  value?: number | null;
  onChange?: (id: number | null, label?: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [input, setInput] = useState("");
  const q = useDebounced(input);
  const query = useDlpDevices({ limit: "20", offset: "0", ...(q ? { q } : {}) });
  const options = (query.data?.items ?? []).map((d) => ({
    value: d.id,
    label: `${d.hostname || d.serial}${d.hostname ? `(${d.serial})` : ""}${d.employee ? ` · ${d.employee.name}` : ""}`,
  }));
  return (
    <Select
      showSearch
      allowClear
      autoFocus={autoFocus}
      style={{ minWidth: 280 }}
      placeholder={placeholder}
      filterOption={false}
      loading={query.isFetching}
      searchValue={input}
      onSearch={setInput}
      value={value ?? undefined}
      options={options}
      onChange={(v, opt) => onChange?.(v ?? null, Array.isArray(opt) ? undefined : opt?.label)}
      notFoundContent={query.isFetching ? "検索中…" : "該当する端末なし"}
    />
  );
}

/** 従業員を選ぶ Select(directory の brief)。スキャン一覧の条件で使う。 */
export function EmployeeSelect({
  value,
  onChange,
  autoFocus,
}: {
  value?: number | null;
  onChange?: (id: number | null, label?: string) => void;
  autoFocus?: boolean;
}) {
  const api = useApi();
  const [input, setInput] = useState("");
  const q = useDebounced(input);
  const query = useQuery({
    queryKey: ["directory", "employees", "dlp-select", q],
    queryFn: () => api.directoryEmployees(new URLSearchParams({ limit: "20", offset: "0", ...(q ? { q } : {}) })),
  });
  const options = (query.data?.items ?? []).map((e) => ({
    value: e.id,
    label: `${e.name}(${e.emp_code})`,
  }));
  return (
    <Select
      showSearch
      allowClear
      autoFocus={autoFocus}
      style={{ minWidth: 280 }}
      placeholder="氏名 / 社員番号"
      filterOption={false}
      loading={query.isFetching}
      searchValue={input}
      onSearch={setInput}
      value={value ?? undefined}
      options={options}
      onChange={(v, opt) => onChange?.(v ?? null, Array.isArray(opt) ? undefined : opt?.label)}
      notFoundContent={query.isFetching ? "検索中…" : "該当する従業員なし"}
    />
  );
}
